// src/components/AdminPanel/AdminLocations.js
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';

function AdminLocations({ adminToken }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ country: '', city: '', area: '' });

  const [cityFilter, setCityFilter] = useState('');

  const fetchLocations = async () => {
    if (!adminToken) return;
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getLocations(adminToken);
      setLocations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line
  }, [adminToken]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const startCreate = () => {
    setFormData({ country: '', city: '', area: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (loc) => {
    setFormData({ country: loc.country, city: loc.city, area: loc.area });
    setEditingId(loc.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ country: '', city: '', area: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { country, city, area } = formData;
    if (!country.trim() || !city.trim() || !area.trim()) {
      setError('Country, city, and area are all required.');
      return;
    }
    setError('');
    setSuccess('');
    try {
      setLoading(true);
      if (editingId) {
        await adminApi.updateLocation(editingId, formData, adminToken);
        setSuccess('Location updated.');
      } else {
        await adminApi.createLocation(formData, adminToken);
        setSuccess('Location created.');
      }
      cancelForm();
      fetchLocations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Delete "${loc.country} / ${loc.city} / ${loc.area}"? This will fail if any venues, users, or gatherings reference it.`)) return;
    setError('');
    setSuccess('');
    try {
      setLoading(true);
      await adminApi.deleteLocation(loc.id, adminToken);
      setSuccess('Location deleted.');
      fetchLocations();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (loc) => {
    setError('');
    setSuccess('');
    try {
      await adminApi.updateLocation(loc.id, {
        country: loc.country,
        city: loc.city,
        area: loc.area,
        is_active: !loc.is_active,
      }, adminToken);
      setSuccess(`"${loc.city} / ${loc.area}" marked ${!loc.is_active ? 'active' : 'inactive'}.`);
      fetchLocations();
    } catch (err) {
      setError(err.message);
    }
  };

  const uniqueCities = [...new Set(locations.map(l => l.city))].sort();
  const displayed = (cityFilter ? locations.filter(l => l.city === cityFilter) : locations)
    .sort((a, b) => a.city.localeCompare(b.city) || a.area.localeCompare(b.area));

  // Options for the cascading form dropdowns (countries → cities → areas)
  const countryOptions = [...new Set(locations.map(l => l.country))].sort();
  const cityOptions = formData.country
    ? [...new Set(locations.filter(l => l.country === formData.country).map(l => l.city))].sort()
    : [];
  const areaOptions = (formData.country && formData.city)
    ? [...new Set(
        locations
          .filter(l => l.country === formData.country && l.city === formData.city)
          .map(l => l.area)
      )].sort()
    : [];

  return (
    <div>
      <h2>Locations (Cities)</h2>

      <div style={{
        backgroundColor: '#fffbeb',
        border: '1px solid #f59e0b',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        marginBottom: '1.25rem',
        fontSize: '0.9rem',
        color: '#92400e',
        lineHeight: '1.5',
      }}>
        <strong>ℹ️ Active / Inactive:</strong> Only <strong>active</strong> locations appear in the city dropdown shown to users during signup and on the Edit Profile page. Mark a location <strong>inactive</strong> to hide it from users without deleting it. Use the toggle button in each row to change the status.
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {success && <p style={{ color: 'green' }}>{success}</p>}

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={startCreate}
          style={{ padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
        >
          + Add Location
        </button>
        <select
          value={cityFilter}
          onChange={e => setCityFilter(e.target.value)}
          style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="">All cities</option>
          {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span style={{ fontSize: '0.9rem', color: '#666' }}>{displayed.length} location(s)</span>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', backgroundColor: '#f9fafb', maxWidth: '480px' }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit Location' : 'New Location'}</h3>

          {editingId ? (
            /* Edit mode: plain text inputs so the admin can rename any value freely */
            <>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0 }}>
                Edit the country, city, or area name directly then click <strong>Update</strong>.
              </p>
              {[['country', 'Country *'], ['city', 'City *'], ['area', 'Area *']].map(([field, label]) => (
                <div key={field} style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem' }}>{label}</label>
                  <input
                    type="text"
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
            </>
          ) : (
            /* Create mode: cascading dropdowns with "add new" option */
            <>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0 }}>
                Pick an existing value from the dropdown, or choose <em>+ Add new…</em> to type a new one.
              </p>
              <DropdownOrNew
                label="Country *"
                value={formData.country}
                options={countryOptions}
                onChange={(v) => setFormData(prev => ({ ...prev, country: v }))}
              />
              <DropdownOrNew
                label="City *"
                value={formData.city}
                options={cityOptions}
                onChange={(v) => setFormData(prev => ({ ...prev, city: v }))}
                disabled={!formData.country}
                disabledHint="Pick or add a country first"
              />
              <DropdownOrNew
                label="Area *"
                value={formData.area}
                options={areaOptions}
                onChange={(v) => setFormData(prev => ({ ...prev, area: v }))}
                disabled={!formData.country || !formData.city}
                disabledHint="Pick or add a city first"
              />
            </>
          )}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" disabled={loading} style={{ padding: '0.5rem 1.25rem', backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>
              {loading ? 'Saving…' : editingId ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={cancelForm} style={{ padding: '0.5rem 1.25rem', backgroundColor: '#6b7280', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && !showForm && <p>Loading…</p>}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f3f4f6' }}>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>ID</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Country</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>City</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Area</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map((loc, idx) => (
            <tr key={loc.id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
              <td style={{ padding: '0.4rem 0.5rem' }}>{loc.id}</td>
              <td style={{ padding: '0.4rem 0.5rem' }}>{loc.country}</td>
              <td style={{ padding: '0.4rem 0.5rem' }}>{loc.city}</td>
              <td style={{ padding: '0.4rem 0.5rem' }}>{loc.area}</td>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                {loc.is_active !== false ? (
                  <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '0.85rem' }}>● Active</span>
                ) : (
                  <span style={{ color: '#9ca3af', fontWeight: '600', fontSize: '0.85rem' }}>○ Inactive</span>
                )}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleToggleActive(loc)}
                  style={{
                    padding: '3px 10px',
                    backgroundColor: '#6b7280',
                    color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem'
                  }}
                >
                  {loc.is_active !== false ? 'Set Inactive' : 'Set Active'}
                </button>
                <button onClick={() => startEdit(loc)} style={{ padding: '3px 10px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Edit</button>
                <button onClick={() => handleDelete(loc)} style={{ padding: '3px 10px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DropdownOrNew({ label, value, options, onChange, disabled = false, disabledHint }) {
  // Mode is "new" if value is non-empty and not in options, otherwise "select"
  const isNew = value !== '' && !options.includes(value);
  const [mode, setMode] = useState(isNew ? 'new' : 'select');

  // Re-sync mode when value/options change externally (e.g. opening edit form)
  useEffect(() => {
    if (value === '') {
      setMode('select');
    } else if (!options.includes(value)) {
      setMode('new');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options.length]);

  const inputStyle = { width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' };

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem' }}>{label}</label>
      {disabled ? (
        <p style={{ fontSize: '0.85rem', color: '#9ca3af', margin: 0 }}>{disabledHint}</p>
      ) : mode === 'select' ? (
        <select
          value={options.includes(value) ? value : ''}
          onChange={e => {
            if (e.target.value === '__add_new__') {
              setMode('new');
              onChange('');
            } else {
              onChange(e.target.value);
            }
          }}
          style={inputStyle}
        >
          <option value="">— Select —</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          <option value="__add_new__" style={{ color: '#16a34a', fontWeight: '700' }}>+ Add new…</option>
        </select>
      ) : (
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="Type new value"
            autoFocus
            required
            style={{ ...inputStyle, flex: 1 }}
          />
          {options.length > 0 && (
            <button
              type="button"
              onClick={() => { setMode('select'); onChange(''); }}
              style={{ padding: '0 0.75rem', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}
              title="Pick from existing instead"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminLocations;
